import React, { useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import CalendarIcon from '../../../CalendarIcon';
import { FormInputText } from '../../../../form-components/FormInputText';
import { updatePassData } from '../../../../api/ticket';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  FormRow,
  DatePickerSeparator,
  ButtonWrapper,
  CancelButton,
  SaveButton,
  PeriodFormLabel,
  AutoApplyButton,
  PeriodDatePickerWrapper,
  PeriodInputWrapper,
} from '../../ChangeTFreezyModalStyled';

export default function ChangePassPeriodModal({ modal, setModal, bigUser, ticket, user, getAllTickets, embedded = false, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: ticket,
  });

  const watchedIssueDate = watch('issueDate');
  const watchedExpireDate = watch('expireDate');

  const isActive = watchedIssueDate && watchedExpireDate;

  // embedded: 상품 정보 수정 모달의 한 탭으로 렌더될 때 — 자체 Modal 껍데기 없이 본문만 그리고 닫기는 부모가 맡는다.
  const onClose = () => {
    if (embedded) {
      onDone?.();
      return;
    }
    setModal({ ...modal, open2: false });
  };

  const submit = async values => {
    const { expireDate, issueDate, memberCoursePassId, memo } = values;

    if (!issueDate) {
      toast.error('시작일자를 입력해주세요');
      return;
    }

    if (!expireDate) {
      toast.error('종료일자를 입력해주세요');
      return;
    }

    if (!validateDateRange(issueDate, expireDate)) {
      toast.error('기간을 다시 확인해주세요');
      return;
    }

    try {
      setIsSubmitting(true);
      await updatePassData(
        user.memberId,
        memberCoursePassId,
        {
          expireDate: moment(expireDate).format('YYYY-MM-DD'),
          startDate: moment(issueDate).format('YYYY-MM-DD'),
          memo,
        },
        bigUser.token,
      );

      toast.success('변경이 완료되었습니다');
      setIsSubmitting(false);
      getAllTickets();
      onClose();
    } catch (error) {
      setIsSubmitting(false);
      toast.error(errorMessages(error));
    }
  };

  const handlePeriodChange = () => {
    ticket.usagePeriodType === 'DAY'
      ? setValue('expireDate', moment(watch('issueDate')).add(ticket.usagePeriod, 'days').toDate())
      : setValue('expireDate', moment(watch('issueDate')).add(ticket.usagePeriod, 'months').toDate());
  };

  const body = (
    <ModalContent>
      <FormSection>
        {/* 이용 기간 */}
        <FormRow>
          <PeriodFormLabel>이용 기간</PeriodFormLabel>
          <PeriodDatePickerWrapper>
            <FormInputDate
              name="issueDate"
              control={control}
              placeholder="YYYY-MM-DD"
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
              renderInput={params => (
                <TextField
                  sx={{
                    width: '163px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '8px',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                  size="small"
                  {...params}
                />
              )}
            />
            <DatePickerSeparator>~</DatePickerSeparator>
            <FormInputDate
              name="expireDate"
              control={control}
              placeholder="YYYY-MM-DD"
              minDate={watchedIssueDate || null}
              disabled={!watchedIssueDate}
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
              renderInput={params => (
                <TextField
                  sx={{
                    width: '163px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '8px',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                  size="small"
                  {...params}
                />
              )}
            />
            <AutoApplyButton onClick={handlePeriodChange}>종료일 자동적용</AutoApplyButton>
          </PeriodDatePickerWrapper>
        </FormRow>

        {/* 메모 */}
        <FormRow>
          <PeriodFormLabel>메모</PeriodFormLabel>
          <PeriodInputWrapper>
            <FormInputText
              control={control}
              name="memo"
              placeholder="변경 사유 메모 (선택)"
            />
          </PeriodInputWrapper>
        </FormRow>
      </FormSection>

      <ButtonWrapper>
        <CancelButton onClick={onClose}>취소</CancelButton>
        <SaveButton
          isActive={isActive && !isSubmitting}
          disabled={!isActive || isSubmitting}
          onClick={handleSubmit(submit)}>
          저장
        </SaveButton>
      </ButtonWrapper>
    </ModalContent>
  );

  if (embedded) {
    return body;
  }

  return (
    <Modal
      open={modal.open2}
      onClose={onClose}
      aria-labelledby="period-modal-title"
      aria-describedby="period-modal-description">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>기간 변경</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {body}
      </ModalContainer>
    </Modal>
  );
}
